/**
 * MovementGoalAnnotation - Independent panel for movement/trajectory goal annotations
 *
 * Wraps TrajectoryGoalEditor in an AnnotationPanel for independent save functionality.
 * Allows coaches to define movement-based constraints (direction, distance, trajectory).
 *
 * This is Type 3 of 3 annotation types:
 * - Body Region: Highlight regions of interest on the body
 * - Distance Goal: Distance constraints between landmarks
 * - Movement Goal: Track landmark movement during phases (this one)
 */

import { useState, useCallback, useEffect } from 'react';
import { ArrowTrendingUpIcon, CursorArrowRaysIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import AnnotationPanel from './AnnotationPanel';
import TrajectoryGoalEditor from './TrajectoryGoalEditor';
import type { TrajectoryConfig } from '@/types/trajectory';
import type { Phase } from '@/types/api';
import { getLandmarkLabel } from '@/utils/landmarkDetection';

// Map mode to human-readable description for auto-label
const getModeDescription = (mode: TrajectoryConfig['mode']): string => {
  switch (mode) {
    case 'direction_only':
      return 'Direction';
    case 'body_relative':
      return 'Body-Relative Distance';
    case 'template_relative':
      return 'Template Match';
    default:
      return 'Movement';
  }
};

// Map direction to human-readable
const getDirectionLabel = (direction?: string): string => {
  if (!direction) return '';
  const labels: Record<string, string> = {
    forward: 'Forward',
    backward: 'Backward',
    up: 'Upward',
    down: 'Downward',
    left: 'Left',
    right: 'Right',
    apart: 'Apart',
    together: 'Together',
  };
  return labels[direction] || direction;
};

interface MovementGoalAnnotationProps {
  // Panel state
  isExpanded: boolean;
  onToggle: () => void;
  panelNumber?: number;
  totalPanels?: number;

  // Movement goal state
  movementGoal: TrajectoryConfig | undefined;
  onMovementGoalChange: (goal: TrajectoryConfig | undefined) => void;

  // Phase context
  phases?: Phase[];
  currentPhase?: Phase | null;
  /** The phase this goal is actually scoped to (contract §1.4) — `null`
   * when the annotation will save with no phase (whole clip), even if
   * `currentPhase` is non-null (the playhead can sit inside a phase while
   * frame-scoping is set to "all"). Used only to wire the travel-distance
   * preview request to the SAME window the save will use. */
  targetPhase?: string | null;

  /** Saved template id — required for the travel-distance preview call
   * (TRAVEL_PR_A_CONTRACT.md §2), which is per-template. Undefined in
   * create-mode (unsaved template): the preview control degrades to "save
   * first" rather than erroring. */
  templateId?: string;

  // Save callback
  onSave: (goal: TrajectoryConfig, label: string, notes: string, importance: number) => Promise<void>;

  /** Forwarded to `TrajectoryGoalEditor` -> `TravelDistanceEditor` — the
   * demo-path overlay (travel/preview-path-points). */
  onPreviewTrajectoryChange?: (points: Array<[number, number, number]> | null) => void;

  // Landmark selection
  onRequestLandmarkSelection?: (callback: (landmark: string) => void) => void;
  selectedLandmark?: string;

  // Status
  isSaved?: boolean;
  isSelectionActive?: boolean;

  className?: string;
}

export default function MovementGoalAnnotation({
  isExpanded,
  onToggle,
  panelNumber = 3,
  totalPanels = 3,
  movementGoal,
  onMovementGoalChange,
  phases,
  currentPhase,
  targetPhase,
  templateId,
  onSave,
  onPreviewTrajectoryChange,
  onRequestLandmarkSelection,
  selectedLandmark,
  isSaved = false,
  isSelectionActive = false,
  className,
}: MovementGoalAnnotationProps) {
  // Local form state
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [importance, setImportance] = useState(50);
  const [isSaving, setIsSaving] = useState(false);
  const [localIsSaved, setLocalIsSaved] = useState(false);

  // Auto-generate label from movement goal configuration
  useEffect(() => {
    if (movementGoal?.enabled && movementGoal?.mode) {
      const modeDesc = getModeDescription(movementGoal.mode);
      const directionLabel = movementGoal.direction?.direction
        ? getDirectionLabel(movementGoal.direction.direction)
        : '';

      // Include landmark name if selected
      const landmarkName = selectedLandmark ? getLandmarkLabel(selectedLandmark) : '';

      // Only auto-set if label is empty or matches previous auto-generated patterns
      const isAutoGenerated = !label ||
        label.startsWith('Direction') ||
        label.startsWith('Body-Relative') ||
        label.startsWith('Template') ||
        label.startsWith('Movement');

      if (isAutoGenerated) {
        const parts = [];
        if (landmarkName) parts.push(landmarkName);
        if (directionLabel) parts.push(directionLabel);
        if (modeDesc) parts.push(modeDesc);

        if (parts.length > 0) {
          setLabel(parts.join(' - '));
        } else {
          setLabel('Movement Goal');
        }
      }
    }
  }, [movementGoal?.enabled, movementGoal?.mode, movementGoal?.direction?.direction, selectedLandmark, label]);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!movementGoal?.enabled || !label.trim()) {
      return;
    }

    setIsSaving(true);
    try {
      await onSave(movementGoal, label.trim(), notes.trim(), importance);

      // Clear form after successful save
      setLabel('');
      setNotes('');
      setImportance(50);
      setLocalIsSaved(true);

      // Clear movement goal after successful save
      onMovementGoalChange(undefined);

      // Reset saved indicator after delay
      setTimeout(() => setLocalIsSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save movement goal annotation:', error);
    } finally {
      setIsSaving(false);
    }
  }, [movementGoal, label, notes, importance, onSave, onMovementGoalChange]);

  // Handle landmark selection request
  const handleSelectLandmark = useCallback(() => {
    if (onRequestLandmarkSelection) {
      onRequestLandmarkSelection((_landmark: string) => {
        // Callback will be called by RegionAnnotator when landmark is selected
        // The selectedLandmark prop will be updated by parent
      });
    }
  }, [onRequestLandmarkSelection]);

  // Can save when movement goal is enabled and configured, we have a landmark, and a label
  const hasCompleteMovementGoal = !!(movementGoal?.enabled && movementGoal?.mode && selectedLandmark);
  const canSave = hasCompleteMovementGoal && !!label.trim();

  return (
    <AnnotationPanel
      title="Movement Goal"
      subtitle="Track motion during phase"
      icon={<ArrowTrendingUpIcon className="w-5 h-5" />}
      accentColor="green"
      panelNumber={panelNumber}
      totalPanels={totalPanels}
      isExpanded={isExpanded}
      onToggle={onToggle}
      label={label}
      onLabelChange={setLabel}
      notes={notes}
      onNotesChange={setNotes}
      importance={importance}
      onImportanceChange={setImportance}
      canSave={canSave}
      isSaving={isSaving}
      isSaved={localIsSaved || isSaved}
      onSave={handleSave}
      saveButtonText="Save Movement Goal"
      className={cn(className, isSelectionActive && 'opacity-50 pointer-events-none')}
    >
      {/* Landmark Selection */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-text-primary mb-2">
          Landmark to Track
        </label>
        <button
          type="button"
          onClick={handleSelectLandmark}
          disabled={!onRequestLandmarkSelection}
          className={cn(
            'w-full flex items-center justify-between px-3 py-2.5 rounded-lg border-2 transition-all text-sm',
            selectedLandmark
              ? 'bg-success/15 border-success/40 text-success'
              : 'bg-dark-elevated-1 border-dashed border-border-subtle text-text-secondary hover:border-success/40 hover:bg-success/10',
            !onRequestLandmarkSelection && 'opacity-50 cursor-not-allowed'
          )}
        >
          <div className="flex items-center gap-2">
            {selectedLandmark ? (
              <>
                <div className="w-3 h-3 rounded-full bg-emerald-500 ring-2 ring-emerald-500/30" />
                <span className="font-medium">{getLandmarkLabel(selectedLandmark)}</span>
              </>
            ) : (
              <>
                <CursorArrowRaysIcon className="h-4 w-4" />
                <span>Click to select landmark</span>
              </>
            )}
          </div>
          {selectedLandmark && (
            <span className="text-xs text-success">Change</span>
          )}
        </button>
        <p className="mt-1.5 text-xs text-text-secondary">
          Select the body landmark you want to track during movement
        </p>
      </div>

      {/* Trajectory Goal Editor */}
      <TrajectoryGoalEditor
        value={movementGoal}
        onChange={onMovementGoalChange}
        hasPhases={!!(phases && phases.length > 0)}
        templateId={templateId}
        trackedLandmark={selectedLandmark}
        trackedLandmarkLabel={selectedLandmark ? getLandmarkLabel(selectedLandmark) : undefined}
        targetPhase={targetPhase}
        onPreviewTrajectoryChange={onPreviewTrajectoryChange}
      />

      {/* Phase Context Info */}
      {currentPhase && (
        <div className="mt-3 p-2 bg-success/10 border border-success/30 rounded-lg text-xs text-success">
          <span className="font-medium">Current Phase:</span> {currentPhase.name}
          <span className="text-success/70 ml-2">
            (frames {currentPhase.start_frame}-{currentPhase.end_frame})
          </span>
        </div>
      )}

      {/* No phases warning */}
      {(!phases || phases.length === 0) && (
        <div className="mt-3 p-3 bg-warning/10 border border-warning/30 rounded-lg text-sm text-warning">
          <strong>Note:</strong> Movement goals work best with defined phases.
          Consider adding phases in Step 2 for more precise tracking.
        </div>
      )}

      {/* Completion status */}
      {hasCompleteMovementGoal && (
        <div className="mt-3 p-3 bg-success/10 border border-success/30 rounded-lg">
          <div className="flex items-center gap-2 text-sm text-success">
            <svg className="h-5 w-5 text-success" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">
              Movement goal configured
            </span>
          </div>
          <p className="text-xs text-success/80 mt-1 ml-7">
            Tracking {selectedLandmark ? getLandmarkLabel(selectedLandmark) : 'landmark'} - {getModeDescription(movementGoal!.mode)}
            {movementGoal!.direction?.direction && ` ${getDirectionLabel(movementGoal!.direction.direction)}`}
          </p>
        </div>
      )}

      {/* Missing landmark warning */}
      {movementGoal?.enabled && !selectedLandmark && (
        <div className="mt-3 p-3 bg-warning/10 border border-warning/30 rounded-lg text-sm text-warning">
          <strong>Note:</strong> Select a landmark to track before saving.
        </div>
      )}
    </AnnotationPanel>
  );
}
